import { defineStore } from "pinia"
import type {UseFetchOptions} from 'nuxt/app'
import type { FetchOptions } from 'ofetch';

type User = {
    id: number;
    company_id: number;
    uuid: string;
    name: string;
    email: string;
    registered_date: string;
    created_at: string;
    updated_at: string;
    memo: string;
}
type OAuthToken = {
    token_type: string;
    expires_in: number;
    access_token: string;
    refresh_token: string;
};

type Credentisals = {
    email: string;
    password: string;
};

// 許可されたHTTPメソッド
type HttpMethod = 'GET' | 'HEAD' | 'PATCH' | 'POST' | 'PUT' | 'DELETE' | 'CONNECT' | 'OPTIONS' | 'TRACE' |
                 'get' | 'head' | 'patch' | 'post' | 'put' | 'delete' | 'connect' | 'options' | 'trace';

// $fetchに渡すオプションの型
interface ApiFetchOptions extends Omit<FetchOptions, 'method'> {
  method?: HttpMethod;
  // watch プロパティは削除
}


export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null);
    const profile = ref<IProfile|null>(null)
    const oauthToken = ref<OAuthToken | null>(null);
    const isLoggedIn = computed(() => !!user.value);
    const contracts = ref<IContract[]>([])

    /*
    async function useAuthApiFetch<T>(path: string, options: UseFetchOptions<T> = {}) {
        let headers: any = {}
        if(!!oauthToken.value){
            headers['Authorization'] = "Bearer "+ oauthToken.value.access_token;
        }
        options.headers = {
            ...headers,
            ...options?.headers
        };
        let result =  await useApiFetch<T>(path,options);

        if(result.error.value?.statusCode == 403){
            useNuxtApp().$forbiddenError();
        }
        return result

    }
    */
    async function useAuthApiFetch<T>(path: string, options: UseFetchOptions<T> = {}) {
        let headers: any = {}
        if(!!oauthToken.value){
            headers['Authorization'] = "Bearer "+ oauthToken.value.access_token ;
        }
        options.headers = {
            ...headers,
            ...options?.headers,

        };
        const result = await useApiFetch<T>(path,options);
        if(result.error.value?.statusCode == 403){
            useNuxtApp().$forbiddenError();
        }
        return result
    }
    /**
     * 認証付きクライアントサイドAPI呼び出し（$fetchベース）
     * マウント後の動的な呼び出しに使用
     */
    async function useAuthApiFetchClient<T>(
        path: string,
        options: Omit<FetchOptions, 'method'> & { method?: HttpMethod } = {}): Promise<T> {
        let headers: any = {}
        // 認証ヘッダーを設定
        if (!!oauthToken.value) {
            headers['Authorization'] = "Bearer " + oauthToken.value.access_token;
        }

        try {
            const result = await useApiFetchClient<T>(path, {
            ...options,
            headers: {
                ...headers,
                ...options?.headers,
            }
            });
            return result;

        } catch (error: any) {
            // 403エラーの場合は禁止エラーハンドラーを呼び出し
            if (error?.statusCode === 403) {
                useNuxtApp().$forbiddenError();
            }else{
                // その他のエラーはAPIエラーハンドラーを呼び出し
                useNuxtApp().$apiError();
            }
            throw error; // エラーを再スローして呼び出し元で処理できるようにする
        }
    }
    async function login(credentials: Credentisals) {

        try {
            // エンドポイントを /oauth/token から /api/user/login に変更
            // client_secret はサーバー側(BFF)で保持し、ブラウザには配信しない
            const login = await useApiFetchClient<OAuthToken>("/api/user/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: {
                    email: credentials.email,
                    password: credentials.password,
                },
            });
            // accessToken,reflashTokenを保持
            oauthToken.value = login as OAuthToken;
            await fetchUser()
            await fetchProfile()
            const {contracts:contracts_result} = await useContract()
            contracts.value = contracts_result ? contracts_result :[]
            return {data:login, error: null};
        }catch (error: any) {
            return {data:null,error:error}
        }

    }
    async function logout() {
        try {
            await useAuthApiFetchClient("/api/user/logout", { method: "POST" });
        } catch (error: any) {
            // ログアウト時のエラーは無視
            console.error("Logout error:", error);
        }finally {
            user.value = null
            oauthToken.value = null
            navigateTo("/login");
        }
    }
    async function fetchUser() {
        try {
            const data = await useAuthApiFetchClient<User>("/api/user");
            user.value = data
        }catch (error: any) {
            useNuxtApp().$apiError();
        }
    }
    async function fetchProfile() {
        try {
            const data = await useAuthApiFetchClient<IProfile>("/api/user/profile");
            profile.value = data;
        }catch (error: any) {
            useNuxtApp().$apiError();
        }
    }
    async function updateProfile(updating:IUpdatingProfile) {
        try {
            const data = await useAuthApiFetchClient<IServiceResult>("/api/user/profile",{
                method:"POST",
                body: updating
            });

            if(data.result){
                await fetchProfile()
                return true
            }else{
                return false
            }
        }catch (error: any) {
            useNuxtApp().$apiError();
            return false
        }
    }
    async function getQrUrl(){
        const { data, error } = await useAuthApiFetch<IQrUrl>("/api/user/qr_url");
        return data.value
    }


    return {
        useAuthApiFetch,
        useAuthApiFetchClient,
        login,
        logout,
        isLoggedIn,
        user,
        profile,
        contracts,
        oauthToken,
        updateProfile,
        getQrUrl
    }
},
    {
        //@ts-ignore
        persist: {storage: sessionStorage}
        //persist: true
    }
)
